<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header detail-page-title-row d-flex justify-content-between align-items-center mb-4">
        <div>
          <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="goBack" style="padding: 10px 0">
            <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
          </button>
          <h3 class="detail-page-title mb-0">{{ subscription?.client?.name }} - {{ $t('subscription_details') }}</h3>
        </div>
        <div class="detail-page-actions">
          <button @click="downloadSubscriptionCard" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
            <AppIcon name="bi-download" class="bi bi-download" style="font-size:12px" />
            {{$t('download_card')}}
          </button>
          <button @click="editSubscription" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
            <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
            {{$t('edit')}}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('loading') }}</span>
        </div>
      </div>



      <!-- Tabs -->
      <ul class="nav nav-tabs detail-page-tabs mb-4" v-if="subscription">
        <li class="nav-item">
          <a class="nav-link" :class="{active: activeTab === 'overview'}" @click="activeTab = 'overview'">{{$t('overview')}}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{active: activeTab === 'attendance'}" @click="activeTab = 'attendance'">{{$t('attendance')}}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{active: activeTab === 'payments'}" @click="activeTab = 'payments'">{{$t('payments')}}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{active: activeTab === 'history'}" @click="activeTab = 'history'">{{$t('history')}}</a>
        </li>
      </ul>

      <!-- Tab Content -->
      <div v-if="activeTab === 'overview' && subscription">

              <!-- Subscription Info Cards -->
      <div v-if="subscription" class="row mb-4">
        <div class="col-md-3">
          <div class="card h-100">
            <div class="card-body">
              <h6 class="text-muted">{{$t('status')}}</h6>
              <span :class="'badge bg-' + getStatusColor(subscription.status)">{{ $t(subscription.status) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100">
            <div class="card-body">
              <h6 class="text-muted">{{$t('plan')}}</h6>
              <h5>{{ subscription.plan?.name || 'N/A' }}</h5>
              <small class="text-muted">{{ subscription.plan?.price }} {{ business.currency }}</small>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100">
            <div class="card-body">
              <h6 class="text-muted">{{$t('duration')}}</h6>
              <h5>{{ calculateDaysRemaining() }} {{$t('days')}}</h5>
              <small class="text-muted">{{$t('remaining')}}</small>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100">
            <div class="card-body">
              <h6 class="text-muted">{{$t('total_visits')}}</h6>
              <h5>{{ attendanceRecords.length }}</h5>
            </div>
          </div>
        </div>
      </div>
        <!-- Insights Cards -->
        <div class="row mb-4">
          <div class="col-md-4">
            <div class="card insight-card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('total_visits')}}</small>
                    <h3 class="mb-0">{{ attendanceRecords.length }}</h3>
                  </div>
                  <div class="insight-icon bg-primary bg-opacity-10 text-primary">
                    <AppIcon name="bi-calendar-check" class="bi bi-calendar-check" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card insight-card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('days_active')}}</small>
                    <h3 class="mb-0">{{ calculateDaysActive() }}</h3>
                  </div>
                  <div class="insight-icon bg-success bg-opacity-10 text-success">
                    <AppIcon name="bi-clock-history" class="bi bi-clock-history" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card insight-card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('days_remaining')}}</small>
                    <h3 class="mb-0">{{ calculateDaysRemaining() }}</h3>
                  </div>
                  <div class="insight-icon bg-warning bg-opacity-10 text-warning">
                    <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Left Column: Membership Card & Details -->
          <div class="col-md-8">
            <!-- Membership Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-card-heading" class="bi bi-card-heading me-2" />{{$t('membership_card')}}</h6>
              </div>
              <div class="card-body">
                <div class="subscription-card" ref="subscriptionCard" :style="cardPreviewStyle">
                  <div class="card-front">
                    <!-- Header with Logo -->
                    <div class="card-header-inner" :style="{ borderBottomColor: cardSettings.textColor + '33' }">
                      <img v-if="cardSettings.showLogo && business.logo" :src="business.logo" alt="Business Logo" class="business-logo" />
                      <div class="company-info">
                        <h3 class="company-name" :style="{ color: cardSettings.textColor }">{{business.name}}</h3>
                        <p class="company-tagline" :style="{ color: cardSettings.textColor }">{{ cardSettings.tagline || 'Premium Membership Card' }}</p>
                      </div>
                    </div>

                    <!-- Main Content -->
                    <div class="card-body-inner">
                      <div class="client-section" :class="{'flex-row-reverse': cardSettings.qrPosition === 'left'}">
                        <div class="client-info">
                          <h2 class="client-name" :style="{ color: cardSettings.textColor }">{{ subscription?.client?.name }}</h2>
                          <div class="membership-info">
                            <div class="info-item">
                              <span class="info-label" :style="{ color: cardSettings.textColor, opacity: 0.7 }">{{ $t('member_since') }}</span>
                              <span class="info-value" :style="{ color: cardSettings.textColor }">{{ formatDate(subscription.start_date) }}</span>
                            </div>
                            <div class="info-item">
                              <span class="info-label" :style="{ color: cardSettings.textColor, opacity: 0.7 }">Member ID</span>
                              <span class="info-value" :style="{ color: cardSettings.textColor }">#{{ subscription?.client?.id }}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div class="qr-section" v-if="cardSettings.showQRCode">
                          <div class="qr-container">
                            <img v-if="qrCodeUrl && qrCodeUrl !== 'NO_CLIENT_ID' && qrCodeUrl !== 'ERROR'" :src="qrCodeUrl" alt="Membership QR Code" class="qr-code" />
                            <div v-else-if="qrCodeUrl === 'NO_CLIENT_ID'" class="qr-loading">
                              <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle text-warning" style="font-size: 2rem;" />
                              <small class="text-muted mt-2">No client ID</small>
                            </div>
                            <div v-else-if="qrCodeUrl === 'ERROR'" class="qr-loading">
                              <AppIcon name="bi-x-circle" class="bi bi-x-circle text-danger" style="font-size: 2rem;" />
                              <small class="text-muted mt-2">Error</small>
                            </div>
                            <div v-else class="qr-loading">
                              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                              <small class="text-muted mt-2">Generating...</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Footer -->
                    <div class="card-footer-inner" v-if="cardSettings.showContactInfo" :style="{ borderTopColor: cardSettings.textColor + '33' }">
                      <div class="contact-info">
                        <span class="contact-item" v-if="business.email" :style="{ color: cardSettings.textColor }">
                          <AppIcon name="bi-envelope" class="bi bi-envelope" />
                          {{business.email}}
                        </span>
                        <span class="contact-item" v-if="business.phone" :style="{ color: cardSettings.textColor }">
                          <AppIcon name="bi-phone" class="bi bi-phone" />
                          {{business.phone}}
                        </span>
                        <span class="contact-item" v-if="business.website" :style="{ color: cardSettings.textColor }">
                          <AppIcon name="bi-globe" class="bi bi-globe" />
                          {{business.website}}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Subscription Details Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />{{$t('subscription_information')}}</h6>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="detail-item">
                      <strong>{{ $t('subscription_reference') }}:</strong>
                      <span class="text-muted">{{ subscription.reference || `#${subscription.id}` }}</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-item">
                      <strong>{{ $t('plan') }}:</strong>
                      <span>{{ subscription.plan?.name }}</span>
                      <small v-if="subscription.plan?.reference" class="text-muted ms-1">({{ subscription.plan?.reference }})</small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-item">
                      <strong>{{ $t('price') }}:</strong>
                      <span class="text-success fw-bold">{{ subscription.plan?.price }} {{ business.currency }}</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-item">
                      <strong>{{ $t('status') }}:</strong>
                      <span :class="'badge bg-' + getStatusColor(subscription.status)">{{ $t(subscription.status) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plan Features Card -->
            <div class="card mb-4" v-if="subscription.plan">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-list-check" class="bi bi-list-check me-2" />{{$t('plan_features')}}</h6>
              </div>
              <div class="card-body">
                <div class="plan-features">
                  <div class="feature-item" v-if="subscription.plan.max_visits_per_day">
                    <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" />
                    <span>{{ subscription.plan.max_visits_per_day }} {{ $t('visits_per_day') }}</span>
                  </div>
                  <div class="feature-item" v-if="subscription.plan.max_visits_per_month">
                    <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" />
                    <span>{{ subscription.plan.max_visits_per_month }} {{ $t('visits_per_month') }}</span>
                  </div>
                  <div class="feature-item" v-if="subscription.plan.access_hours_start && subscription.plan.access_hours_end">
                    <AppIcon name="bi-clock" class="bi bi-clock text-info" />
                    <span>{{ $t('access_hours') }}: {{ subscription.plan.access_hours_start }} - {{ subscription.plan.access_hours_end }}</span>
                  </div>
                  <div class="feature-item" v-if="subscription.plan.description">
                    <AppIcon name="bi-info-circle" class="bi bi-info-circle text-primary" />
                    <span>{{ subscription.plan.description }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes Card -->
            <div v-if="subscription.notes" class="card">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
              </div>
              <div class="card-body">
                <p class="mb-0">{{ subscription.notes }}</p>
              </div>
            </div>
          </div>

          <!-- Right Column: Client Info & Dates -->
          <div class="col-md-4">
            <!-- Dates Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-calendar-event" class="bi bi-calendar-event me-2" />{{$t('important_dates')}}</h6>
              </div>
              <div class="card-body">
                <div class="mini-timeline">
                  <div class="mini-timeline-item">
                    <div class="mini-timeline-marker bg-primary"></div>
                    <div class="mini-timeline-content">
                      <small class="text-muted d-block">{{$t('start_date')}}</small>
                      <strong class="d-block">{{ formatDate(subscription.start_date) }}</strong>
                    </div>
                  </div>

                  <div class="mini-timeline-item">
                    <div class="mini-timeline-marker bg-success"></div>
                    <div class="mini-timeline-content">
                      <small class="text-muted d-block">{{$t('current_status')}}</small>
                      <strong class="d-block">{{ $t(subscription.status) }}</strong>
                      <small class="text-muted">{{ calculateDaysRemaining() }} {{$t('days_remaining')}}</small>
                    </div>
                  </div>

                  <div class="mini-timeline-item">
                    <div class="mini-timeline-marker bg-warning"></div>
                    <div class="mini-timeline-content">
                      <small class="text-muted d-block">{{$t('end_date')}}</small>
                      <strong class="d-block">{{ formatDate(subscription.end_date) }}</strong>
                    </div>
                  </div>

                  <div class="mini-timeline-item" v-if="subscription.created_at">
                    <div class="mini-timeline-marker bg-info"></div>
                    <div class="mini-timeline-content">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong class="d-block text-info">{{ formatDateTime(subscription.created_at) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Client Card -->
            <div class="card mb-4" v-if="subscription.client">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-person-badge" class="bi bi-person-badge me-2" />{{$t('client_information')}}</h6>
              </div>
              <div class="card-body">
                <div class="client-details">
                  <div class="avatar-circle mb-3">
                    <AppIcon name="bi-person" class="bi bi-person" />
                  </div>
                  <h6 class="mb-2">{{ subscription.client.name }}</h6>
                  <div class="client-contact">
                    <small class="d-block text-muted" v-if="subscription.client.email">
                      <AppIcon name="bi-envelope" class="bi bi-envelope me-1" />{{ subscription.client.email }}
                    </small>
                    <small class="d-block text-muted" v-if="subscription.client.phone">
                      <AppIcon name="bi-phone" class="bi bi-phone me-1" />{{ subscription.client.phone }}
                    </small>
                    <small class="d-block text-muted mt-2" v-if="subscription.client.address">
                      <AppIcon name="bi-geo-alt" class="bi bi-geo-alt me-1" />{{ subscription.client.address }}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Summary Card -->
            <div class="card mb-4" v-if="transactions && transactions.length > 0">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-credit-card" class="bi bi-credit-card me-2" />{{$t('payment_summary')}}</h6>
              </div>
              <div class="card-body">
                <div class="d-flex justify-content-between mb-2">
                  <strong>{{ $t('total_paid') }}:</strong>
                  <span class="text-success">{{ formatMoney(totalPaid) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <strong>{{ $t('subscription_amount') }}:</strong>
                  <span>{{ formatMoney(subscription?.plan?.price || 0) }}</span>
                </div>
                <hr>
                <div class="d-flex justify-content-between" v-if="remainingBalance > 0">
                  <strong>{{ $t('remaining_balance') }}:</strong>
                  <span class="text-danger fw-bold">{{ formatMoney(remainingBalance) }}</span>
                </div>
                <div class="d-flex justify-content-between" v-else>
                  <strong>{{ $t('payment_status') }}:</strong>
                  <span class="badge bg-success">{{ $t('fully_paid') }}</span>
                </div>
                <div class="mt-3">
                  <small class="text-muted">
                    <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                    {{ $t('view_all_payments_in_payments_tab') }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Attendance Tab -->
            <!-- Attendance Tab -->
      <div v-if="activeTab === 'attendance' && subscription" class="card">
        <div class="card-body">
          <h5 class="mb-3">{{$t('attendance_records')}}</h5>
          <table-loading-bar v-if="loadingAttendance" class="text-center py-4" />
          <div v-if="!loadingAttendance" class="table-responsive">
            <table class="table table-sm table-hover">
              <thead>
                <tr>
                  <th>{{$t('date')}}</th>
                  <th>{{$t('check_in')}}</th>
                  <th>{{$t('check_out')}}</th>
                  <th>{{$t('duration')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in attendanceRecords" :key="record.id">
                  <td>{{ formatDate(record.check_in) }}</td>
                  <td>{{ formatTime(record.check_in) }}</td>
                  <td>{{ record.check_out ? formatTime(record.check_out) : '-' }}</td>
                  <td>{{ calculateDuration(record.check_in, record.check_out) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!loadingAttendance && attendanceRecords.length === 0" class="text-center py-4 text-muted">
            <AppIcon name="bi-calendar-x" class="bi bi-calendar-x" style="font-size: 3rem;" />
            <p class="mt-2">{{ $t('no_attendance_records') }}</p>
          </div>
        </div>
      </div>

      <!-- Payments Tab -->
      <div v-if="activeTab === 'payments' && subscription">
        <payment-component
          :paymentable_id="subscription.id"
          paymentable_type="subscription"
          :showFilters="true"
        />
      </div>

      <!-- History Tab -->

      <!-- History Tab -->
      <div v-if="activeTab === 'history' && subscription">
        <document-history :history="historyRecords" :loading="loadingHistory" />
      </div>

      <!-- Not Found State -->
      <div v-if="!loading && !subscription" class="text-center py-5">
        <AppIcon name="bi-exclamation-circle" class="bi bi-exclamation-circle text-danger" style="font-size: 3rem;" />
        <p class="mt-2">{{ $t('subscription_not_found') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import helper from '../helper';
import PaymentComponent from '../components/PaymentComponent.vue';
import DocumentHistory from '../components/DocumentHistory.vue';

export default {
  name: 'SubscriptionDetails',
  components: {
    PaymentComponent,
    DocumentHistory
  },
  data() {
    return {
      subscription: null,
      loading: true,
      qrCodeUrl: '',
      attendanceRecords: [],
      historyRecords: [],
      transactions: [],
      loadingAttendance: false,
      loadingHistory: false,
      activeTab: 'overview',
      cardTemplate: null
    }
  },
  computed: {
    business() { return this.$store.getters.BUSINESS },
    statusColorClass() {
      if (!this.subscription) return '';
      const status = this.subscription.status;
      return {
        'text-success': status === 'active',
        'text-danger': status === 'canceled',
        'text-secondary': status === 'expired'
      };
    },
    totalPaid() {
      if (!this.transactions || this.transactions.length === 0) return 0;
      return this.transactions
        .filter(t => t.status === 'paid')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    },
    remainingBalance() {
      const planPrice = parseFloat(this.subscription?.plan?.price || 0);
      return Math.max(0, planPrice - this.totalPaid);
    },
    cardSettings() {
      if (!this.cardTemplate) {
        return {
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          borderRadius: 15,
          tagline: 'Premium Member',
          showLogo: true,
          showQRCode: true,
          showContactInfo: true,
          qrPosition: 'right'
        };
      }
      try {
        const settings = typeof this.cardTemplate === 'string' 
          ? JSON.parse(this.cardTemplate) 
          : this.cardTemplate;
        return settings;
      } catch (error) {
        alert('Error parsing card template:', error);
        return {
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          borderRadius: 15,
          tagline: 'Premium Member',
          showLogo: true,
          showQRCode: true,
          showContactInfo: true,
          qrPosition: 'right'
        };
      }
    },
    cardPreviewStyle() {
      return {
        background: this.cardSettings.backgroundColor,
        color: this.cardSettings.textColor,
        borderRadius: `${this.cardSettings.borderRadius}px`
      };
    }
  },
  methods: {
    goBack() {
      this.$router.push({ path: '/subscriptions' });
    },
    getStatusColor(status) {
      const colors = {
        active: 'success',
        canceled: 'danger',
        expired: 'secondary',
        paused: 'warning'
      }
      return colors[status] || 'secondary'
    },
    calculateDaysActive() {
      if (!this.subscription || !this.subscription.start_date) return 0;
      const now = new Date();
      const startDate = new Date(this.subscription.start_date);
      const diffTime = now - startDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },
    async fetchSubscription() {
      try {
        this.loading = true;
        const subscriptionId = this.$route.params.id;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/subscriptions/${subscriptionId}`);
        this.subscription = response.data;
        
        // Generate QR code
        await this.generateQRCode();
        
        // Fetch additional data
        this.fetchAttendanceRecords();
        this.fetchHistoryRecords();
        this.fetchTransactions();
        this.fetchCardTemplate();
      } catch (error) {
        window.vueOpener.open({
          message: error.message || this.$t('failed_to_fetch_subscription'),
          type: 'error',
          position: 'bottom'
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchCardTemplate() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/templates`);
        const templates = response?.data?.data || [];
        const membershipCardTemplate = templates.find(t => t.type === 'membership_card');
        if (membershipCardTemplate) {
          this.cardTemplate = membershipCardTemplate.data;
        }
      } catch (error) {
        alert('Error fetching card template:' + error.message || error);
      }
    },
    async generateQRCode() {
      if (!this.subscription) {
        return;
      }
      
      let clientId = null;
      
      if (this.subscription?.client?.id) {
        clientId = this.subscription.client.id;
      } else if (this.subscription?.client_id) {
        clientId = this.subscription.client_id;
      }
      
      if (!clientId) {
        this.qrCodeUrl = 'NO_CLIENT_ID';
        return;
      }
      
      this.qrCodeUrl = '';
      await this.$nextTick();
      
      try {
        const qrData = String(clientId);
        this.qrCodeUrl = await QRCode.toDataURL(qrData, {
          width: 150,
          margin: 1,
          color: {
            dark: '#2c3e50',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'M'
        });
        
      } catch (error) {
        alert('Error generating QR code: ' + (error.message || error));
        this.qrCodeUrl = 'ERROR';
      }
    },
    async fetchAttendanceRecords() {
      try {
        this.loadingAttendance = true;
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/attendance?subscription_id=${this.subscription.id}&limit=100`
        );
        this.attendanceRecords = response.data.data || [];
      } catch (error) {
        alert('Error fetching attendance records: ' + (error.message || error));
        this.attendanceRecords = [];
      } finally {
        this.loadingAttendance = false;
      }
    },
    async fetchHistoryRecords() {
      try {
        this.loadingHistory = true;
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/subscriptions/${this.subscription.id}/history`
        );
        // Backend now returns an array directly
        this.historyRecords = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        alert('Error fetching history records: ' + (error.message || error));
        this.historyRecords = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    async fetchTransactions() {
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/transactions?paymentable_type=subscription&paymentable_id=${this.subscription.id}`
        );
        this.transactions = response.data.data || [];
      } catch (error) {
        alert('Error fetching transactions: ' + (error.message || error));
        this.transactions = [];
      }
    },
    formatMoney(number) {
      return helper.formatMoney(number);
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString();
    },
    formatDateTime(date) {
      return new Date(date).toLocaleString();
    },
    calculateDaysRemaining() {
      if (!this.subscription || !this.subscription.end_date) return 0;
      const now = new Date();
      const endDate = new Date(this.subscription.end_date);
      const diffTime = endDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },
    calculateDuration(checkIn, checkOut) {
      if (!checkOut) return '-';
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffMs = end - start;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    },
    getHistoryTypeClass(type) {
      const classes = {
        'created': 'bg-success',
        'updated': 'bg-info',
        'paused': 'bg-warning',
        'resumed': 'bg-info',
        'frozen': 'bg-primary',
        'canceled': 'bg-danger',
        'renewed': 'bg-success'
      };
      return classes[type] || 'bg-secondary';
    },
    async downloadSubscriptionCard() {
      try {
        const element = this.$refs.subscriptionCard;
        const canvas = await html2canvas(element, {
          scale: 2,
          backgroundColor: null,
          removeContainer: true
        });
        
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png', 0.8);
        link.download = `membership-card-${this.subscription.client?.name?.replace(/\s+/g, '-').toLowerCase()}-${this.subscription.client?.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.vueOpener.open({
          message: this.$t('card_downloaded_successfully'),
          type: 'success',
          position: 'bottom'
        });
      } catch (err) {
        alert('Error downloading card: ' + (err.message || err));
        window.vueOpener.open({
          message: this.$t('error_downloading_card'),
          type: 'error',
          position: 'bottom'
        });
      }
    },
    editSubscription() {
      this.$router.push({ path: `/subscriptions/${this.subscription.id}/edit` });
    }
  },
  mounted() {
    window.mixpanel.track("subscription_details");
    this.fetchSubscription();
  }
}
</script>

<style scoped>
.nav-link {
  cursor: pointer;
}

/* Insight Cards */
.insight-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.insight-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Avatar Circle */
.avatar-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  margin: 0 auto;
}

/* Mini Timeline for Dates */
.mini-timeline {
  position: relative;
  padding-left: 25px;
}

.mini-timeline::before {
  content: '';
  position: absolute;
  left: 8px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: linear-gradient(to bottom, #0d6efd, #ffc107, #198754);
}

.mini-timeline-item {
  position: relative;
  margin-bottom: 20px;
  padding-left: 5px;
}

.mini-timeline-item:last-child {
  margin-bottom: 0;
}

.mini-timeline-marker {
  position: absolute;
  left: -21px;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #e9ecef;
}

.mini-timeline-content {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s;
}

.mini-timeline-content:hover {
  background: #e9ecef;
  transform: translateX(3px);
}

.mini-timeline-content small {
  font-size: 11px;
  margin-bottom: 2px;
}

.mini-timeline-content strong {
  font-size: 13px;
}

/* Card Headers */
.card-header {
  border-bottom: 2px solid #f0f0f0;
}

.card-header h6 {
  color: #495057;
  font-weight: 600;
}

/* Membership Card Styles */
.subscription-card {
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.card-front {
  padding: 2rem;
}

.card-header-inner {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid;
}

.business-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  background: white;
  border-radius: 12px;
  padding: 0.5rem;
}

.company-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.company-tagline {
  margin: 0;
  opacity: 0.9;
  font-size: 0.75rem;
}

.card-body-inner {
  padding: 1rem 0;
}

.client-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.client-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.membership-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 600;
}

.qr-section {
  flex-shrink: 0;
}

.qr-container {
  background: white;
  padding: 0.75rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  min-width: 140px;
}

.qr-code {
  width: 120px;
  height: 120px;
  display: block;
}

.qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #6c757d;
}

.card-footer-inner {
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid;
}

.contact-info {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  font-size: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-item i {
  font-size: 0.875rem;
}

/* Detail Items */
.detail-item {
  padding: 0.5rem 0;
}

.detail-item strong {
  color: #495057;
  font-weight: 600;
}

/* Plan Features */
.plan-features {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.feature-item i {
  font-size: 1.25rem;
}

/* History Timeline */
.history-timeline {
  position: relative;
  padding-left: 30px;
}

.history-timeline::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #0d6efd, #198754);
}

.history-item {
  position: relative;
  margin-bottom: 20px;
}

.history-item:last-child {
  margin-bottom: 0;
}

.history-marker {
  position: absolute;
  left: -26px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px #e9ecef;
}

.history-content {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #0d6efd;
}

.history-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
}

.history-date {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 5px;
}

.history-description {
  font-size: 13px;
  margin-bottom: 0;
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

/* Responsive */
@media (max-width: 768px) {
  .client-section {
    flex-direction: column;
  }
  
  .qr-container {
    align-self: center;
  }
  
  .info-card {
    padding: 1rem;
  }
  
  .card-body-inner {
    padding: 1rem;
  }
  
  .detail-item strong {
    display: block;
    margin-bottom: 0.25rem;
  }
}
</style>
